import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { IconButton, MenuItem } from '@mui/material';
import clsx from 'clsx';
import {
  endOfMonth,
  endOfToday,
  endOfWeek,
  endOfYesterday,
  format,
  isValid,
  startOfMonth,
  startOfToday,
  startOfWeek,
  startOfYesterday,
  subDays,
  subMonths,
  subWeeks,
  isAfter,
} from 'date-fns';
import { capitalize, isArray } from 'lodash';
import { shallowEqual, useSelector } from 'react-redux';
import { useControlled } from '@mui/material/utils';

import { useBreakpoints } from 'helpers/styles';

import WrapIf from 'components/WrapIf';
import Button from 'components/mui/Button';
import BlIcon from 'components/icons/BlIcon';
import { currentMonth, monthForward } from 'helpers/dates';
import DialogTitle from 'components/mui/DialogTitle';
import { DAY, MONTH, RANGE } from 'consts';
import { selectMe } from 'store/slices/accountSlice';

import s from './styled';

export const timeframeItems = [
  {
    type: DAY,
    label: 'Today',
    value: [startOfToday(), endOfToday()],
  },
  {
    type: DAY,
    label: 'Yesterday',
    value: [startOfYesterday(), endOfYesterday()],
  },
  {
    type: RANGE,
    label: 'Last Week',
    value: [
      startOfWeek(subWeeks(startOfToday(), 1)),
      endOfWeek(subWeeks(startOfToday(), 1)),
    ],
  },
  {
    type: MONTH,
    label: 'This Month',
    value: [startOfMonth(startOfToday()), endOfMonth(startOfToday())],
  },
  {
    type: MONTH,
    label: 'Last Month',
    value: [
      startOfMonth(subMonths(startOfToday(), 1)),
      endOfMonth(subMonths(startOfToday(), 1)),
    ],
  },
  {
    type: RANGE,
    label: 'Last 7 Days',
    value: [subDays(startOfToday(), 7), endOfToday()],
  },
  {
    type: RANGE,
    label: 'Last 30 Days',
    value: [subDays(startOfToday(), 30), endOfToday()],
  },
  {
    id: 'custom',
    type: RANGE,
    label: 'Custom Date Range',
    value: null,
  },
];

const Timeframe = memo(function Timeframe(props) {
  const {
    className,
    value: valueProp,
    defaultValue,
    onChange,
    nullable,
    label = 'Timeframe',
    timezone: timezoneProp,
  } = props;

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && !nullable && !valueProp) {
      throw new Error(
        'If component is not nullable, you should pass a "value" prop',
      );
    }
  }, [nullable, valueProp]);

  const { isMdUp } = useBreakpoints();
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const me = useSelector(selectMe, shallowEqual);
  let timezone = me?.timezone;
  if (timezoneProp === null) {
    timezone = timezoneProp;
  }

  const [value, setValue] = useControlled({
    controlled: getTimeframeItem(valueProp)?.value || valueProp,
    default: getTimeframeItem(defaultValue)?.value || defaultValue,
    name: 'Timeframe',
  });

  const [prevNotCustomValue, setPrevNotCustomValue] = useState(value);
  const [mainItems, restItems] = useMemo(() => {
    return [timeframeItems.slice(0, 3), timeframeItems.slice(3)];
  }, []);

  /**
   * Select
   */
  const [selectAnchorEl, setSelectAnchorEl] = useState(null);

  const onOpenMenu = useCallback((event) => {
    setSelectAnchorEl(event.currentTarget);
  }, []);

  const onCloseMenu = useCallback(() => {
    setSelectAnchorEl(null);
  }, []);

  /**
   * Datepicker
   */
  const initialCustomDateRange = useMemo(() => {
    const isEmptyValue = isArray(value) ? value.some((item) => !item) : !value;
    if (!!getTimeframeItem(value)?.value || isEmptyValue) {
      return [currentMonth, monthForward];
    }

    return value;
  }, [value]);

  /**
   * Used to attach values to DatePicker,
   * so it won't run onChange everytime when the range changed.
   */
  const [customDateRangeInner, setCustomDateRangeInner] = useState(
    initialCustomDateRange,
  );

  const onChangeRangeInner = useCallback((range) => {
    setCustomDateRangeInner(range);
  }, []);

  /**
   * Real date range
   */
  const [customDateRange, setCustomDateRange] = useState(customDateRangeInner);

  const onChangeRange = useCallback(() => {
    setCustomDateRange(customDateRangeInner);
    setValue(customDateRangeInner);
    onChange?.(customDateRangeInner);
  }, [onChange, customDateRangeInner, setValue]);

  const buttonGroupRef = useRef(null);
  const datePickerAnchorRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const createOnTimePeriodChange = (item) => () => {
    onCloseMenu();

    let newTimePeriod = item.value;

    if (newTimePeriod === value) {
      if (nullable) {
        newTimePeriod = null;
      } else {
        return;
      }
    }

    if (item.id === 'custom') {
      onOpenDatepicker();

      newTimePeriod = customDateRange;
    } else {
      setPrevNotCustomValue(newTimePeriod);
    }

    setValue(newTimePeriod);
    onChange?.(newTimePeriod);
  };

  const onOpenDatepicker = useCallback(() => {
    const isEmptyRange = customDateRangeInner.some((item) => !item);
    if (isEmptyRange) {
      setCustomDateRangeInner(initialCustomDateRange);
      setCustomDateRange(initialCustomDateRange);
      const customItem = timeframeItems.find((tItem) => !tItem.value);
      createOnTimePeriodChange(customItem, initialCustomDateRange);
    }

    setDatePickerOpen(true);
  }, [initialCustomDateRange, customDateRangeInner, createOnTimePeriodChange]);

  const onCloseDatepicker = useCallback(() => {
    setDatePickerOpen(false);
    const [startDate, endDate] = customDateRangeInner;
    const isValidDates =
      isValid(startDate) && isValid(endDate) && !isAfter(startDate, endDate);
    if (!isValidDates) {
      onChange?.(prevNotCustomValue);
    } else {
      onChangeRange();
    }
  }, [customDateRangeInner, onChange, prevNotCustomValue, onChangeRange]);

  const isCustomRangeSelected = value === customDateRange;

  const checkValueSelected = useCallback(
    (item) => {
      return (
        (item.id === 'custom' && isCustomRangeSelected) ||
        JSON.stringify(value) === JSON.stringify(item.value)
      );
    },
    [isCustomRangeSelected, value],
  );

  const selectedItem = useMemo(
    () => timeframeItems.find(checkValueSelected),
    [checkValueSelected],
  );

  const selectedRestItem = useMemo(
    () => restItems.find(checkValueSelected),
    [restItems, checkValueSelected],
  );

  /**
   * Mobile dialog
   */
  const [openMobileDialog, setOpenMobileDialog] = useState(false);

  const onOpenMobileDialog = useCallback(() => {
    setOpenMobileDialog(true);
  }, []);

  const onCloseMobileDialog = useCallback(() => {
    setOpenMobileDialog(false);

    if (isCustomRangeSelected) {
      const [startDate, endDate] = customDateRangeInner;
      const isValidDates =
        isValid(startDate) && isValid(endDate) && !isAfter(startDate, endDate);
      if (!isValidDates) {
        onChange?.(prevNotCustomValue);
      } else {
        onChangeRange();
      }
    }
  }, [
    isCustomRangeSelected,
    onChangeRange,
    customDateRangeInner,
    onChange,
    prevNotCustomValue,
  ]);

  return (
    <s.TimeframeWrapper className={className}>
      {isMdUp && (
        <>
          <div>
            <s.TimeframeLabel variant="h6" component="span">
              {label}:
            </s.TimeframeLabel>
            {timezone && (
              <s.TimeframeTimezone variant="caption">
                {timezone}
              </s.TimeframeTimezone>
            )}
          </div>

          <s.Menu
            elevation={3}
            anchorEl={selectAnchorEl}
            keepMounted
            open={Boolean(selectAnchorEl)}
            onClose={onCloseMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            {restItems.map((item) => (
              <MenuItem
                key={item.label}
                selected={checkValueSelected(item)}
                onClick={createOnTimePeriodChange(item)}
              >
                {item.label}
              </MenuItem>
            ))}
          </s.Menu>

          <s.TimeframeButtonGroup ref={buttonGroupRef}>
            {mainItems.map((item) => (
              <s.TimeframeButton
                key={item.label}
                value={item.label}
                onClick={createOnTimePeriodChange(item)}
                $selected={value === item.value}
                className={clsx('Timeframe-button', {
                  'Timeframe-selected': value === item.value,
                })}
              >
                {item.label}
              </s.TimeframeButton>
            ))}

            <s.TimeframeButton
              onClick={onOpenMenu}
              $selected={!!selectedRestItem}
              className={clsx('Timeframe-button', {
                'Timeframe-selected': !!selectedRestItem,
              })}
              $select
              endIcon={
                <IconButton size="small" color="inherit" component="span">
                  <BlIcon icon="arrow-down" fontSize="small" />
                </IconButton>
              }
            >
              Other - {selectedRestItem?.label || restItems?.[0]?.label}
            </s.TimeframeButton>

            {isCustomRangeSelected && (
              <s.TimeframeButton
                $selected
                className={clsx('Timeframe-button', 'Timeframe-selected')}
                data-selected
                onClick={onOpenDatepicker}
                ref={datePickerAnchorRef}
              >
                <>
                  {isValid(customDateRange?.[0])
                    ? format(customDateRange?.[0], 'MM/dd/yyyy')
                    : customDateRange?.[0]?.toString()}{' '}
                  -{' '}
                  {isValid(customDateRange?.[1])
                    ? format(customDateRange?.[1], 'MM/dd/yyyy')
                    : customDateRange?.[1]?.toString()}{' '}
                </>
              </s.TimeframeButton>
            )}
          </s.TimeframeButtonGroup>
        </>
      )}

      {isMdUp && datePickerOpen && (
        <s.DatePopover
          anchorEl={datePickerAnchorRef.current || buttonGroupRef.current}
          open={datePickerOpen}
          className="Timeframe-datePopover"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          onClose={onCloseDatepicker}
        >
          <s.TimeframeDatepicker
            value={customDateRangeInner}
            onChange={onChangeRangeInner}
          />
        </s.DatePopover>
      )}

      {!isMdUp && (
        <>
          <s.TimeframeButtonMobile
            fullWidth
            variant="outlined"
            endIcon={<BlIcon icon="arrow-down" />}
            onClick={onOpenMobileDialog}
          >
            {capitalize(label)}: {selectedItem?.label}
          </s.TimeframeButtonMobile>

          <WrapIf
            Wrapper={s.Dialog}
            if={!isMdUp}
            fullScreen
            open={openMobileDialog}
            onClose={onCloseMobileDialog}
          >
            <DialogTitle>Choose a timeframe:</DialogTitle>
            <s.DialogContent>
              <s.TimeframeRadioGroup aria-label="radios" name="radio">
                {timeframeItems.map((item) => {
                  return (
                    <s.TimeframeSwitchItem
                      key={item.label}
                      radio
                      fullWidth
                      label={item.label}
                      value={
                        item.id === 'custom' ? customDateRangeInner : item.value
                      }
                      checked={checkValueSelected(item)}
                      onChange={createOnTimePeriodChange(item)}
                    />
                  );
                })}
              </s.TimeframeRadioGroup>

              {isCustomRangeSelected && (
                <s.TimeframeDatepicker
                  value={customDateRangeInner}
                  onChange={onChangeRangeInner}
                  singleView={!isMdUp}
                  labelStart="Start Date"
                  labelEnd="End Date"
                  placeholder="10/10/2018"
                />
              )}
            </s.DialogContent>
            <s.DialogActions>
              <Button fullWidth onClick={onCloseMobileDialog}>
                Done
              </Button>
            </s.DialogActions>
          </WrapIf>
        </>
      )}
    </s.TimeframeWrapper>
  );
});

Timeframe.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string,
  timezone: PropTypes.string,
  value: PropTypes.array,
  defaultValue: PropTypes.array,
  onChange: PropTypes.func,
  nullable: PropTypes.bool,
};

export function getTimeframeItem(range, fallbackToCustom = true) {
  if (!range) return;

  const found = timeframeItems.find((item) => {
    return JSON.stringify(range) === JSON.stringify(item.value);
  });

  if (fallbackToCustom) {
    return found || timeframeItems.find((item) => item.id === 'custom');
  }

  return found;
}

export default Timeframe;
